import { useCallback } from 'react';
import { ResizeObserver } from '@juggle/resize-observer';
import { localPoint } from '@visx/event';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';

import type { AnyObj } from '@blockworks/platform';

const DEFAULT_TOOLTIP_STYLES = {
    ...defaultStyles,
    padding: '8px',
    borderRadius: '8px',
    width: 152,
    height: 72,
};

type UseVisualizationTooltipOptions = {
    useLocalPoint?: boolean;
};

const useVisualizationTooltip = <T = any>({ useLocalPoint }: UseVisualizationTooltipOptions = {}) => {
    const { tooltipData, tooltipOpen, tooltipLeft, tooltipTop, showTooltip, hideTooltip } = useTooltip<T>();

    const { containerRef, containerBounds, TooltipInPortal } = useTooltipInPortal({
        detectBounds: true,
        scroll: true,
        polyfill: ResizeObserver,
    });

    const handleShowTooltip = useCallback(
        (event: React.MouseEvent, tooltipData: T) => {
            if (useLocalPoint) {
                const target = event.target as SVGElement;
                const svg = target.ownerSVGElement;
                if (svg) {
                    const coords = localPoint(svg, event);
                    showTooltip({
                        tooltipData,
                        tooltipTop: (coords?.y ?? 0) + 10,
                        tooltipLeft: (coords?.x ?? 0) + 10,
                    });
                }
                return;
            }

            showTooltip({
                tooltipData,
                tooltipLeft: ('clientX' in event ? event.clientX : 0) - containerBounds.left,
                tooltipTop: ('clientY' in event ? event.clientY : 0) - containerBounds.top,
            });
        },
        [showTooltip, useLocalPoint, containerBounds],
    );

    return {
        tooltipOpen,
        containerRef,
        containerBounds,
        tooltipData,
        tooltipLeft,
        tooltipTop,
        hideTooltip,
        showTooltip: handleShowTooltip,
        TooltipInPortal,
    };
};

useVisualizationTooltip.DEFAULT_STYLES = DEFAULT_TOOLTIP_STYLES;

type UseVisualizationTooltipReturn<T extends AnyObj> = ReturnType<typeof useVisualizationTooltip<T>>;

export type { UseVisualizationTooltipReturn };
export { useVisualizationTooltip };
